import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, X, AlertCircle, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';

interface Template {
  id: number;
  code: string;
  serviceType?: 'B2C' | 'B2B' | 'BOTH';
  channel: 'sms' | 'email' | 'push' | 'webhook';
  subject?: string;
  body: string;
  variables?: string[];
  isActive: boolean;
}

function ServiceBadge({ value }: { value?: string }) {
  const v = (value ?? 'BOTH').toUpperCase();
  const cls = v === 'B2C' ? 'bg-blue-50 text-blue-700' : v === 'B2B' ? 'bg-purple-50 text-purple-700' : 'bg-slate-100 text-slate-600';
  return <span className={`text-xs px-2 py-0.5 rounded font-medium ${cls}`}>{v === 'BOTH' ? 'Both' : v}</span>;
}
interface Trigger { code: string; name: string; channels: string[]; vars: string[]; }

export function NotificationTemplatesTab({ channel }: { channel: 'sms' | 'email' }) {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['notif-templates', channel],
    queryFn: async () => (await api.get<Template[]>('/notification-templates', { params: { channel } })).data,
  });
  const { data: triggers = [] } = useQuery({
    queryKey: ['notif-triggers'],
    queryFn: async () => (await api.get<Trigger[]>('/notification-templates/triggers')).data,
  });

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/notification-templates/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['notif-templates'] }); },
  });

  const Icon = channel === 'sms' ? MessageSquare : Mail;
  const title = channel === 'sms' ? 'SMS Templates' : 'Email Templates';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Icon size={18} /> {title}</h2>
          <p className="text-xs text-slate-500 mt-0.5">Create templates and link them to system triggers (OTP, order booked, delivered, etc.)</p>
        </div>
        <button onClick={() => { setEditing(null); setShow(true); }}
          className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm flex items-center gap-1.5">
          <Plus size={14} /> Add Template
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Trigger / Code</th>
              <th className="text-left px-3 py-2">Service</th>
              {channel === 'email' && <th className="text-left px-3 py-2">Subject</th>}
              <th className="text-left px-3 py-2">Body Preview</th>
              <th className="text-left px-3 py-2">Variables</th>
              <th className="text-center px-3 py-2">Active</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="text-center py-6 text-slate-400">Loading…</td></tr>}
            {!isLoading && items.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-400">No templates yet. Add one to enable {channel.toUpperCase()} notifications.</td></tr>}
            {items.map((t) => (
              <tr key={t.id} className="border-t hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">
                  <div>{triggers.find(g => g.code === t.code)?.name ?? t.code}</div>
                  <div className="text-xs text-slate-400 font-mono">{t.code}</div>
                </td>
                <td className="px-3 py-2"><ServiceBadge value={t.serviceType} /></td>
                {channel === 'email' && <td className="px-3 py-2 text-slate-600">{t.subject ?? '—'}</td>}
                <td className="px-3 py-2 text-slate-600 max-w-md truncate">{t.body.slice(0, 90)}{t.body.length > 90 ? '…' : ''}</td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {(t.variables ?? []).map(v => <code key={v} className="bg-slate-100 px-1 py-0.5 rounded mr-1">{`{${v}}`}</code>)}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded ${t.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{t.isActive ? 'On' : 'Off'}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => { setEditing(t); setShow(true); }} className="p-1.5 rounded hover:bg-slate-100"><Pencil size={13} /></button>
                    <button onClick={() => { if (confirm(`Delete "${t.code}"?`)) remove.mutate(t.id); }} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <TemplateForm
          channel={channel}
          initial={editing}
          triggers={triggers}
          existingItems={items}
          onClose={() => { setShow(false); setEditing(null); }}
          onSaved={() => { setShow(false); setEditing(null); qc.invalidateQueries({ queryKey: ['notif-templates'] }); }}
        />
      )}
    </div>
  );
}

function TemplateForm({ channel, initial, triggers, existingItems, onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({
    code: initial?.code ?? '',
    serviceType: initial?.serviceType ?? 'BOTH',
    subject: initial?.subject ?? '',
    body: initial?.body ?? '',
    variables: initial?.variables ?? [],
    isActive: initial?.isActive ?? true,
  });
  const [err, setErr] = useState<string | null>(null);

  // A trigger can have one template per service type — exclude triggers already used for the selected type.
  const available: Trigger[] = useMemo(() => {
    const used = new Set(
      (existingItems as Template[])
        .filter((it) => !(initial && it.id === initial.id))
        .map((it) => `${it.code}|${(it.serviceType ?? 'BOTH')}`)
    );
    return (triggers as Trigger[]).filter(t => t.channels.includes(channel) && !used.has(`${t.code}|${form.serviceType}`));
  }, [triggers, existingItems, channel, initial, form.serviceType]);

  const onPickTrigger = (code: string) => {
    const t = triggers.find((g: Trigger) => g.code === code);
    if (!t) return;
    setForm({ ...form, code, variables: t.vars });
  };

  const insertVar = (v: string) => {
    setForm({ ...form, body: (form.body ?? '') + `{${v}}` });
  };

  const save = useMutation({
    mutationFn: async () => {
      setErr(null);
      if (!form.code) throw new Error('Pick a trigger or enter a custom code');
      if (!form.body || form.body.length < 1) throw new Error('Body cannot be empty');
      if (channel === 'email' && !form.subject) throw new Error('Email subject is required');
      const payload = { ...form, channel };
      if (initial) return (await api.patch(`/notification-templates/${initial.id}`, payload)).data;
      return (await api.post('/notification-templates', payload)).data;
    },
    onSuccess: () => { toast.success(initial ? 'Template updated' : 'Template created'); onSaved(); },
    onError: (e: any) => {
      const m = e?.response?.data?.message ?? e?.message ?? 'Failed';
      const t = Array.isArray(m) ? m.join(', ') : String(m);
      setErr(t); toast.error(t);
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[760px] max-h-[90vh] overflow-y-auto">
        <div className="bg-indigo-700 text-white px-5 py-3 flex items-center justify-between rounded-t">
          <div className="font-semibold">{initial ? 'Edit' : 'Add'} {channel.toUpperCase()} Template</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {err && (
          <div className="mx-5 mt-4 bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /><div>{err}</div>
          </div>
        )}
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Trigger *</label>
              {initial ? (
                <input value={form.code} disabled className="w-full border rounded px-2 py-1.5 text-sm bg-slate-50 font-mono" />
              ) : (
                <select value={form.code} onChange={(e) => onPickTrigger(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                  <option value="">— Select a trigger —</option>
                  {available.map((t: Trigger) => <option key={t.code} value={t.code}>{t.name} ({t.code})</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-600">Service Type *</label>
              <select value={form.serviceType} onChange={(e) => setForm({ ...form, serviceType: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="BOTH">Both (B2C &amp; B2B)</option>
                <option value="B2C">B2C only</option>
                <option value="B2B">B2B only</option>
              </select>
            </div>
          </div>
          {channel === 'email' && (
            <div>
              <label className="text-xs text-slate-600">Subject *</label>
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="e.g. Your order {orderNo} is booked"
                className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
          )}
          <div>
            <div className="flex justify-between items-end mb-1">
              <label className="text-xs text-slate-600">Message Body *</label>
              <div className="text-xs text-slate-400">Click a variable to insert</div>
            </div>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={channel === 'email' ? 10 : 5}
              placeholder={channel === 'email' ? 'Use {variables} where dynamic values should appear' : 'Max 160 chars for SMS'}
              className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
            <div className="flex flex-wrap gap-1 mt-2">
              {(form.variables ?? []).map((v: string) => (
                <button key={v} onClick={() => insertVar(v)} type="button"
                  className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded hover:bg-indigo-100">{`{${v}}`}</button>
              ))}
            </div>
            {channel === 'sms' && (
              <div className="text-xs text-slate-500 mt-1">{form.body?.length ?? 0} / 160 chars · ~{Math.ceil((form.body?.length ?? 0) / 160)} segment(s)</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            <label className="text-sm">Active (template will be used)</label>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">Cancel</button>
          <button onClick={() => save.mutate()} disabled={save.isPending} className="bg-indigo-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
